import React, { useState } from "react";
import { Card, Flex, Text, Button, Badge } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { AuthLeatDialog } from "./AuthLeatDialog";
import { LeatChoiceDialog } from "./LeatChoiceDialog";
import { LeatMappingDialog } from "./LeatMappingDialog";
import { useValidateLeat, useGetLeatStatus } from "hooks";
import { PermissionAlertDialog } from "components";
import { useToast } from "../../../context/ToastContext";

export function BrowseLeatCard({ userProfile }) {
  const { setToastMessage } = useToast();
  const queryClient = useQueryClient();
  const [apiKey, setApiToken] = useState("");
  const [isApiTokenValid, setIsApiTokenValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
  const [pendingChoiceData, setPendingChoiceData] = useState(null);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [shopsForMapping, setShopsForMapping] = useState([]);
  const [initialMappings, setInitialMappings] = useState([]);
  const [initialFieldMappings, setInitialFieldMappings] = useState(null);
  const [reconnectAction, setReconnectAction] = useState(null);
  const validateLeat = useValidateLeat();
  const { data: leatStatusData } = useGetLeatStatus(userProfile.organisation_id);

  const isLeatActive = () => {
    return leatStatusData?.data?.is_active === true;
  };

  const handleOpenChange = (isOpen) => {
    setOpenDialog(isOpen);
    if (!isOpen) {
      handleCancelAuthDialog();
    }
  };

  const handleConnectClick = () => {
    const isOwner = userProfile.user_role === "Owner";

    if (isOwner) {
      handleOpenChange(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  const handleCancelAuthDialog = () => {
    setApiToken("");
    setIsApiTokenValid(true);
  };

  const handleApiTokenChange = (event) => {
    setApiToken(event.target.value);
    setIsApiTokenValid(true);
  };

  const openMappingStep = (shops, action = null, locationMappings = [], fieldMappings = null) => {
    setShopsForMapping(Array.isArray(shops) ? shops : []);
    setReconnectAction(action);
    setInitialMappings(Array.isArray(locationMappings) ? locationMappings : []);
    setInitialFieldMappings(fieldMappings);
    setOpenDialog(false);
    setChoiceDialogOpen(false);
    setMappingDialogOpen(true);
  };

  const handleSave = async (choice = null) => {
    if (!apiKey) {
      setIsApiTokenValid(false);
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        api_token: apiKey,
        organisation_id: userProfile.organisation_id,
      };

      if (choice) payload.action_choice = choice;

      const result = await validateLeat.mutateAsync(payload);
      const status = result?.data?.status;

      if (status === "pending_user_choice") {
        setPendingChoiceData(result.data);
        setChoiceDialogOpen(true);
        return;
      }

      if (status === "success" || status === "restored" || status === "resumed") {
        queryClient.invalidateQueries(["leat-status"]);
        queryClient.invalidateQueries(["leat-mappings", userProfile.organisation_id]);
        openMappingStep(
          result.data.shops,
          result.data.action || null,
          result.data.location_mappings || [],
          result.data.field_mappings || null
        );
        return;
      }

      if (status === "invalid" || status === "error" || result?.data?.invalid === "api_token") {
        setIsApiTokenValid(false);
      }
    } catch (error) {
      console.error("Validation error:", error);
      const invalidField = error?.response?.data?.invalid;
      const status = error?.response?.status;
      if (invalidField === "api_token" || status === 401) {
        setIsApiTokenValid(false);
      } else {
        setToastMessage("Unexpected error. Please try again.");
        setIsApiTokenValid(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card
        style={{
          display: "flex",
          minWidth: "455px",
          padding: "var(--space-4)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="4">
          <Flex
            height="40px"
            width="40px"
            p="2"
            style={{
              borderRadius: "var(--space-1)",
              background: "#FFFFFF",
              border: "1px solid var(--gray-a6)",
            }}
          >
            <img
              src={require("../../../assets/images/leat.jpeg")}
              alt="Leat"
              style={{ width: "24px" }}
            />
          </Flex>
          <Flex direction="column" justify="center" align="start">
            <Text size="4" weight="medium">
              Leat
            </Text>
            <Text size="1" color="gray">
              Loyalty & CRM
            </Text>
          </Flex>
        </Flex>

        <Flex align="center">
          {isLeatActive() ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button onClick={handleConnectClick} size="2" variant="soft" color="gray">
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      <AuthLeatDialog
        isOpen={openDialog}
        onOpenChange={handleOpenChange}
        onCancel={handleCancelAuthDialog}
        onSave={handleSave}
        handleApiTokenChange={handleApiTokenChange}
        isApiTokenValid={isApiTokenValid}
        isLoading={isLoading}
      />

      <LeatChoiceDialog
        isOpen={choiceDialogOpen}
        onOpenChange={setChoiceDialogOpen}
        data={pendingChoiceData}
        onChoose={(choice) => {
          setChoiceDialogOpen(false);
          handleSave(choice);
        }}
      />

      <LeatMappingDialog
        isOpen={mappingDialogOpen}
        onOpenChange={setMappingDialogOpen}
        shops={shopsForMapping}
        initialMappings={initialMappings}
        initialFieldMappings={initialFieldMappings}
        reconnectAction={reconnectAction}
      />

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
